import React, { useState, useRef, useEffect } from 'react';
import { Paper, TextField, Button, List, ListItem, ListItemText, CircularProgress } from '@material-ui/core';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { MarkdownContent } from '@backstage/core-components';

type Message = {
  id: number;
  from: 'user' | 'bot';
  text: string;
  mcpToolsUsed?: string[];
};

type ChatHistoryTurn = {
  role: 'user' | 'assistant';
  content: string;
};

type ChatBoxProps = {
  initialMessages?: Message[];
  height?: number | string;
  placeholder?: string;
  useCaseId?: string;
};

export const ChatBox = ({ initialMessages = [], height = 160, placeholder = 'Ask for workflow guidance...', useCaseId = '1' }: ChatBoxProps) => {
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const nextId = useRef(messages.length + 1);
  const listRef = useRef<HTMLDivElement | null>(null);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    
    const userMsg: Message = { id: nextId.current++, from: 'user', text };
    setMessages(m => [...m, userMsg]);
    setInput('');
    setLoading(true);

    try {
      // Route through the Backstage proxy instead of a hardcoded localhost URL.
      // This works in all environments (local, Docker, K8s) and respects TLS.
      const proxyBase = await discoveryApi.getBaseUrl('proxy');
      const response = await fetchApi.fetch(`${proxyBase}/llm/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: text,
          useCaseId: useCaseId,
          chatHistory: messages.map<ChatHistoryTurn>(message => ({
            role: message.from === 'user' ? 'user' : 'assistant',
            content: message.text,
          })),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get response from LLM service');
      }

      const data = await response.json();
      const botMsg: Message = {
        id: nextId.current++,
        from: 'bot',
        text: data.answer,
        mcpToolsUsed: Array.isArray(data.mcpToolsUsed) ? data.mcpToolsUsed : [],
      };
      setMessages(m => [...m, botMsg]);
    } catch (error) {
      console.error('Error calling LLM service:', error);
      const errorMsg: Message = {
        id: nextId.current++,
        from: 'bot',
        text: 'Sorry, I encountered an error. Please make sure the LLM service is running and reachable via the Backstage proxy.',  
      };
      setMessages(m => [...m, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  const onKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <Paper elevation={0} style={{ height, display: 'flex', flexDirection: 'column', padding: 8 }}>
      <div ref={listRef} style={{ overflowY: 'auto', flex: 1, padding: '4px 8px' }}>
            <List dense>
              {messages.map(m => (
                <ListItem key={m.id} alignItems="flex-start">
                  <ListItemText
                    primary={m.from === 'user' ? 'You' : 'CODE4EV Assistant'}
                    secondaryTypographyProps={{ component: 'div' }}
                    secondary={
                      m.from === 'bot' ? (
                        <div style={{ wordBreak: 'break-word' }}>
                          <MarkdownContent content={m.text} />
                          {m.mcpToolsUsed && m.mcpToolsUsed.length > 0 && (
                            <div style={{ marginTop: 8, fontSize: '0.75rem', color: '#666' }}>
                              MBSE MCP tools used: {m.mcpToolsUsed.join(', ')}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {m.text}
                        </span>
                      )
                    }
                  />
                </ListItem>
              ))}
            </List>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <TextField
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyPress={onKeyPress}
          placeholder={placeholder}
          variant="outlined"
          size="small"
          fullWidth
          disabled={loading}
          inputProps={{ 'aria-label': 'chat-input' }}
        />
        <Button 
          variant="contained" 
          color="primary" 
          onClick={send} 
          disabled={loading}
          aria-label="Send chat message"
        >
          {loading ? <CircularProgress size={24} /> : 'Send'}
        </Button>
      </div>
    </Paper>
  );
};

export default ChatBox;
